/**
 * Phase 18 M1: form state -> request data. Only filled fields are sent; a blank number is
 * left out, never sent as 0. Also the reverse (entry -> form) for edit and "Copy last".
 */
import type {
  EntryData,
  FeedData,
  GeneralNoteData,
  IncidentData,
  MeasurementData,
  MedicationData,
  OutputData,
  RespiratoryData,
  SeizureData,
  SupervisoryNoteData,
  TherapyTreatmentData,
  AideTasksData,
  AideTaskStatus,
  VisitSummaryData,
  VitalsData,
  WoundCareData,
  MarStatus,
  MarWithholdReason,
  MarEffectiveness,
  NoteCategory,
  PainScale,
  TeachingMethod,
  TeachingUnderstanding,
  RespiratoryMode,
  SecretionAmount,
  SeizureIntervention,
  OutputType,
  SupervisedDiscipline,
  PocFollowed,
  ClientSatisfaction,
  FeedType,
  FeedTolerance,
  IncidentSeverity,
} from '../../../../dtos/patientmr/charting';
import { NOTE_CATEGORIES } from '../../../../dtos/patientmr/charting/general-note.dto';
import { observedIsoFromTime, toClockTime, VisitWindow } from '../../../../utils/observedAt';
import { NOT_ON_MED_LIST } from '../types';
import type {
  Entry,
  FeedFormData,
  IncidentFormData,
  MarFormData,
  MeasurementsFormData,
  NoteFormData,
  OutputFormData,
  RespiratoryFormData,
  SeizureFormData,
  SupervisoryFormData,
  TherapyFormData,
  AideTasksFormData,
  VitalFormData,
  WoundFormData,
} from '../types';
import {
  emptyFeed,
  emptyIncident,
  emptyMar,
  emptyMeasurements,
  emptyNote,
  emptyOutput,
  emptyRespiratory,
  emptySeizure,
  emptySupervisory,
  emptyTherapy,
  emptyAideTasks,
  emptyVitals,
  emptyWound,
  EntryFormBundle,
} from './entryForms';
import { seizureDurationSeconds } from './entryValidation';
import { buildTemplateNote, noteTemplateFor } from './noteTemplates';

const text = (v: string | null | undefined): string | undefined => {
  const t = typeof v === 'string' ? v.trim() : '';
  return t ? t : undefined;
};
const numberOf = (v: string | null | undefined): number | undefined => {
  const t = typeof v === 'string' ? v.trim() : '';
  if (!t) return undefined;
  const n = Number(t);
  return Number.isFinite(n) ? n : undefined;
};
const listOf = (v: string): string[] | undefined => {
  const items = v.split(',').map((s) => s.trim()).filter(Boolean);
  return items.length ? items : undefined;
};
/** Drop undefined keys so the request holds only what was entered */
const compact = <T extends object>(obj: T): T =>
  Object.fromEntries(Object.entries(obj).filter(([, v]) => v !== undefined)) as T;

const str = (v: unknown): string => (v === undefined || v === null ? '' : String(v));

export interface BuildContext {
  /** Visit window used to turn clock times (seizure start) into ISO times */
  window: VisitWindow;
}

export function buildVitals(v: VitalFormData): VitalsData {
  const bp = text(v.bloodPressure) || (text(v.bloodPressureSystolic) && text(v.bloodPressureDiastolic) ? `${v.bloodPressureSystolic.trim()}/${v.bloodPressureDiastolic.trim()}` : undefined);
  const score = numberOf(v.painScore);
  return compact<VitalsData>({
    temperature: numberOf(v.temperature),
    bloodPressure: bp,
    pulse: numberOf(v.pulse),
    respiratoryRate: numberOf(v.respiratoryRate),
    o2Saturation: numberOf(v.o2Saturation),
    pain: score !== undefined && text(v.painScale) ? { scale: v.painScale as PainScale, score } : undefined,
    o2FlowLpm: numberOf(v.o2FlowLpm),
  });
}

export function buildMedication(m: MarFormData): MedicationData {
  // MED-03: a picked medication sends its id; free text sends only the name (server sets notOnMedList)
  const pickedId = m.medicationId && m.medicationId !== NOT_ON_MED_LIST ? m.medicationId : undefined;
  const base = compact<MedicationData>({
    medicationId: pickedId,
    medicationName: text(m.medicationName) || '',
    dose: text(`${m.doseAmount} ${m.doseUnit}`),
    route: text(m.route),
    administrationTime: text(m.administrationTime),
    notes: text(m.notes),
    indication: text(m.indication),
  });
  if (text(m.followUpOf)) {
    return compact<MedicationData>({ ...base, followUpOf: m.followUpOf, effectiveness: (text(m.effectiveness) as MarEffectiveness) || undefined });
  }
  return compact<MedicationData>({
    ...base,
    status: m.status,
    withholdReason: m.status !== 'GIVEN' ? ((text(m.withholdReason) as MarWithholdReason) || undefined) : undefined,
    site: text(m.site),
    reaction: text(m.reaction),
  });
}

export function buildNote(n: NoteFormData): GeneralNoteData {
  const t = n.teaching;
  return compact<GeneralNoteData>({
    note: n.note.trim(),
    category: n.category,
    followUpNeeded: false,
    visibility: n.visibility,
    teaching: n.category === 'TEACHING'
      ? {
          subject: t.subject.trim(),
          learner: t.learner.trim(),
          method: t.method as TeachingMethod,
          durationMinutes: numberOf(t.durationMinutes) as number,
          understanding: t.understanding as TeachingUnderstanding,
        }
      : undefined,
  });
}

export function buildWound(w: WoundFormData): WoundCareData {
  const dims = compact({ length: numberOf(w.length), width: numberOf(w.width), depth: numberOf(w.depth) });
  return compact<WoundCareData>({
    woundLocation: text(w.woundLocation),
    woundDescription: w.woundDescription.trim(),
    drainage: text(w.drainage),
    treatmentPerformed: text(w.treatmentPerformed),
    woundMeasurements: Object.keys(dims).length ? { ...dims, units: w.units || 'cm' } : undefined,
  });
}

export function buildIncident(i: IncidentFormData): IncidentData {
  return compact<IncidentData>({
    incidentType: text(i.incidentType),
    severity: (text(i.severity) as IncidentSeverity) || undefined,
    description: i.description.trim(),
    // M6: internalOnly is kept for compatibility and mirrors visibility
    internalOnly: i.internalOnly,
    visibility: i.internalOnly ? 'INTERNAL' : 'CHART',
    actionsTaken: text(i.actionsTaken),
    reportedTo: text(i.reportedTo),
    witnesses: listOf(i.witnesses),
    injuryDetails: text(i.injuryDetails),
  });
}

export function buildMeasurements(m: MeasurementsFormData): MeasurementData {
  return compact({
    height: numberOf(m.height),
    weight: numberOf(m.weight),
    headCircumference: numberOf(m.headCircumference),
    heightUnit: m.heightUnit || 'in',
    weightUnit: m.weightUnit || 'lb',
    headCircumferenceUnit: m.headCircumferenceUnit || 'in',
  }) as MeasurementData;
}

export function buildFeed(d: FeedFormData): FeedData {
  const notes = text(d.notes);
  return compact<FeedData>({
    feedType: (text(d.feedType) as FeedType) || undefined,
    formula: text(d.formula),
    volumeMl: numberOf(d.volumeMl),
    durationMinutes: numberOf(d.durationMinutes),
    rateMlPerHour: numberOf(d.rateMlPerHour),
    residualMl: numberOf(d.residualMl),
    tolerance: (text(d.tolerance) as FeedTolerance) || undefined,
    // older readers show feedDetails
    notes,
    feedDetails: notes,
  });
}

export function buildRespiratory(r: RespiratoryFormData): RespiratoryData {
  return compact<RespiratoryData>({
    mode: r.mode as RespiratoryMode,
    ventMode: text(r.ventMode),
    rate: numberOf(r.rate),
    tidalVolumeMl: numberOf(r.tidalVolumeMl),
    pip: numberOf(r.pip),
    peep: numberOf(r.peep),
    fio2: numberOf(r.fio2),
    o2FlowLpm: numberOf(r.o2FlowLpm),
    spo2: numberOf(r.spo2),
    suctionCount: numberOf(r.suctionCount),
    secretionAmount: (text(r.secretionAmount) as SecretionAmount) || undefined,
    secretionColor: text(r.secretionColor),
    tolerance: text(r.tolerance),
    alarmsChecked: r.alarmsChecked || undefined,
    circuitChecked: r.circuitChecked || undefined,
    notes: text(r.notes),
  });
}

export function buildSeizure(s: SeizureFormData, ctx: BuildContext): SeizureData {
  const secs = seizureDurationSeconds(s.durationMinutes, s.durationSeconds);
  return compact<SeizureData>({
    startedAt: observedIsoFromTime(s.startedTime, ctx.window) || '',
    durationSeconds: secs === null || Number.isNaN(secs) ? undefined : secs,
    seizureType: text(s.seizureType),
    description: text(s.description),
    interventions: s.interventions.length ? (s.interventions as SeizureIntervention[]) : undefined,
    rescueMedEntryId: text(s.rescueMedEntryId),
    postIctalState: text(s.postIctalState),
    notified: listOf(s.notified),
    notes: text(s.notes),
  });
}

export function buildOutput(o: OutputFormData): OutputData {
  return compact<OutputData>({
    outputType: o.outputType as OutputType,
    volumeMl: numberOf(o.volumeMl),
    diaperCount: numberOf(o.diaperCount),
    characteristics: text(o.characteristics),
    notes: text(o.notes),
  });
}

export function buildSupervisory(s: SupervisoryFormData): SupervisoryNoteData {
  return compact<SupervisoryNoteData>({
    supervisedUserId: s.supervisedUserId,
    supervisedDiscipline: s.supervisedDiscipline as SupervisedDiscipline,
    staffPresent: s.staffPresent === 'YES',
    pocFollowed: s.pocFollowed as PocFollowed,
    competenciesObserved: s.competenciesObserved.length ? s.competenciesObserved : undefined,
    clientSatisfaction: (text(s.clientSatisfaction) as ClientSatisfaction) || undefined,
    issues: text(s.issues),
    actions: text(s.actions),
    nextDueDate: text(s.nextDueDate),
  });
}

/** Phase 20C: blank lines and goals are dropped; minutes are whole numbers */
export function buildTherapy(t: TherapyFormData): TherapyTreatmentData {
  const lines = t.lines
    .filter((l) => l.code.trim())
    .map((l) => compact<{ code: string; minutes?: number }>({ code: l.code.trim(), minutes: numberOf(l.minutes) }));
  const goals = t.goalsAddressed
    .filter((g) => g.goal.trim())
    .map((g) => compact<{ goal: string; goalId?: string; progress: string; note?: string }>({
      goal: g.goal.trim(), goalId: text(g.goalId), progress: g.progress, note: text(g.note),
    }));
  return compact<TherapyTreatmentData>({
    evaluationCode: text(t.evaluationCode),
    lines: lines.length ? lines : undefined,
    interventions: text(t.interventions),
    goalsAddressed: goals.length ? (goals as TherapyTreatmentData['goalsAddressed']) : undefined,
    homeProgram: text(t.homeProgram),
    patientResponse: text(t.patientResponse),
    coTreatment: t.coTreatment,
    coTreatmentWith: t.coTreatment ? text(t.coTreatmentWith) : undefined,
    telehealth: t.telehealth,
  });
}

/** Phase 20D: blank task rows are dropped; minutes are whole numbers */
export function buildAideTasks(a: AideTasksFormData): AideTasksData {
  return compact<AideTasksData>({
    tasks: a.tasks
      .filter((t) => t.task.trim())
      .map((t) => compact<{ task: string; status: AideTaskStatus; minutes?: number; note?: string }>({
        task: t.task.trim(), status: t.status as AideTaskStatus, minutes: numberOf(t.minutes), note: text(t.note),
      })),
    observations: text(a.observations),
    reportedToSupervisor: a.reportedToSupervisor,
  });
}

/** Request data for a local entry type from the form bundle */
export function buildEntryData(type: string, f: EntryFormBundle, ctx: BuildContext): EntryData {
  switch (type) {
    case 'vitals': return buildVitals(f.vitals);
    case 'mar': return buildMedication(f.mar);
    case 'notes': return buildNote(f.notes);
    case 'wound': return buildWound(f.wound);
    case 'incident': return buildIncident(f.incident);
    case 'measurements': return buildMeasurements(f.measurements);
    case 'summary': return { note: f.summary.trim() } as VisitSummaryData;
    case 'feed': return buildFeed(f.feed);
    case 'respiratory': return buildRespiratory(f.respiratory);
    case 'seizure': return buildSeizure(f.seizure, ctx);
    case 'output': return buildOutput(f.output);
    case 'supervisory': return buildSupervisory(f.supervisory);
    case 'therapy': return buildTherapy(f.therapy);
    case 'aidetasks': return buildAideTasks(f.aidetasks);
    default: {
      const template = noteTemplateFor(type);
      if (template) return buildTemplateNote(template, f.templatenote) as EntryData;
      throw new Error(`Unknown entry type: ${type}`);
    }
  }
}

// ---------------------------------------------------------------------------------------------
// Entry -> form (edit, "Copy last", "Record effect")
// ---------------------------------------------------------------------------------------------

type Data = Record<string, unknown>;
const dataOf = (entry: Pick<Entry, 'data'>): Data =>
  entry.data && typeof entry.data === 'object' ? (entry.data as Data) : {};

export function vitalsForm(entry: Pick<Entry, 'data'>): VitalFormData {
  const d = dataOf(entry);
  const pain = d.pain;
  const painObj = pain && typeof pain === 'object' ? (pain as { scale?: unknown; score?: unknown }) : null;
  return {
    ...emptyVitals(),
    temperature: str(d.temperature),
    bloodPressure: str(d.bloodPressure),
    pulse: str(d.pulse),
    respiratoryRate: str(d.respiratoryRate),
    o2Saturation: str(d.o2Saturation),
    painScale: painObj ? str(painObj.scale) : '',
    painScore: painObj ? str(painObj.score) : typeof pain === 'number' ? String(pain) : '',
    o2FlowLpm: str(d.o2FlowLpm),
  };
}

export function marForm(entry: Pick<Entry, 'data'>, fallbackTime: string): MarFormData {
  const d = dataOf(entry);
  const doseParts = str(d.dose).trim().split(/\s+/);
  const status = str(d.status) as MarStatus;
  return {
    ...emptyMar(fallbackTime),
    // Entries without an id (free text, or older entries) edit as "Not on med list"
    medicationId: str(d.medicationId) || (d.notOnMedList || d.medicationName ? NOT_ON_MED_LIST : ''),
    medicationName: str(d.medicationName),
    doseAmount: doseParts[0] || '',
    doseUnit: doseParts.slice(1).join(' '),
    route: str(d.route),
    administrationTime: str(d.administrationTime) || fallbackTime,
    notes: str(d.notes) || str(d.note),
    status: status === 'HELD' || status === 'REFUSED' || status === 'NOT_GIVEN' ? status : 'GIVEN',
    withholdReason: str(d.withholdReason),
    indication: str(d.indication),
    effectiveness: str(d.effectiveness),
    followUpOf: str(d.followUpOf),
    site: str(d.site),
    reaction: str(d.reaction),
  };
}

/** M7 "Record effect": a follow-up of a PRN GIVEN entry, med prefilled */
export function marFollowUpForm(entry: Pick<Entry, 'id' | 'data'>, nowTime: string): MarFormData {
  const base = marForm(entry, nowTime);
  return {
    ...base,
    administrationTime: nowTime,
    notes: '',
    status: 'GIVEN',
    withholdReason: '',
    effectiveness: '',
    followUpOf: String(entry.id),
    site: '',
    reaction: '',
  };
}

export function feedForm(entry: Pick<Entry, 'data'>): FeedFormData {
  if (typeof entry.data === 'string') return { ...emptyFeed(), notes: entry.data };
  const d = dataOf(entry);
  return {
    feedType: str(d.feedType),
    formula: str(d.formula),
    volumeMl: str(d.volumeMl),
    durationMinutes: str(d.durationMinutes),
    tolerance: str(d.tolerance),
    notes: str(d.notes) || str(d.feedDetails),
    rateMlPerHour: str(d.rateMlPerHour),
    residualMl: str(d.residualMl),
  };
}

export function noteForm(entry: Pick<Entry, 'data'>): NoteFormData {
  if (typeof entry.data === 'string') return { ...emptyNote(), note: entry.data };
  const d = dataOf(entry);
  const category = (NOTE_CATEGORIES as readonly string[]).includes(str(d.category)) ? (str(d.category) as NoteCategory) : 'GENERAL';
  const t = d.teaching && typeof d.teaching === 'object' ? (d.teaching as Data) : {};
  return {
    note: str(d.note),
    category,
    visibility: d.visibility === 'INTERNAL' ? 'INTERNAL' : 'CHART',
    teaching: {
      subject: str(t.subject),
      learner: str(t.learner),
      method: str(t.method),
      durationMinutes: str(t.durationMinutes),
      understanding: str(t.understanding),
    },
  };
}

export function woundForm(entry: Pick<Entry, 'data'>): WoundFormData {
  if (typeof entry.data === 'string') return { ...emptyWound(), woundDescription: entry.data };
  const d = dataOf(entry);
  const m = d.woundMeasurements && typeof d.woundMeasurements === 'object' ? (d.woundMeasurements as Data) : {};
  // Older entries stored 0 for "not measured"
  const dim = (v: unknown) => (v === 0 || v === '0' ? '' : str(v));
  return {
    woundLocation: str(d.woundLocation),
    woundDescription: str(d.woundDescription),
    drainage: str(d.drainage),
    treatmentPerformed: str(d.treatmentPerformed),
    length: dim(m.length),
    width: dim(m.width),
    depth: dim(m.depth),
    units: str(m.units) || 'cm',
  };
}

export function incidentForm(entry: Pick<Entry, 'data'>): IncidentFormData {
  if (typeof entry.data === 'string') return { ...emptyIncident(), description: entry.data };
  const d = dataOf(entry);
  return {
    incidentType: str(d.incidentType),
    severity: str(d.severity),
    description: str(d.description),
    internalOnly: d.internalOnly === true || d.visibility === 'INTERNAL',
    actionsTaken: str(d.actionsTaken),
    reportedTo: str(d.reportedTo),
    witnesses: Array.isArray(d.witnesses) ? d.witnesses.map(String).join(', ') : str(d.witnesses),
    injuryDetails: str(d.injuryDetails),
  };
}

export function measurementsForm(entry: Pick<Entry, 'data'>): MeasurementsFormData {
  const d = dataOf(entry);
  // Older entries stored 0 for "not measured"
  const val = (v: unknown) => (v === 0 || v === null ? '' : str(v));
  return {
    ...emptyMeasurements(),
    height: val(d.height),
    heightUnit: str(d.heightUnit) || 'in',
    weight: val(d.weight),
    weightUnit: str(d.weightUnit) || 'lb',
    headCircumference: val(d.headCircumference),
    headCircumferenceUnit: str(d.headCircumferenceUnit) || 'in',
  };
}

export function summaryForm(entry: Pick<Entry, 'data'>): string {
  if (typeof entry.data === 'string') return entry.data;
  const d = dataOf(entry);
  return str(d.note) || str(d.situation) || str(d.assessment);
}

export function respiratoryForm(entry: Pick<Entry, 'data'>): RespiratoryFormData {
  const d = dataOf(entry);
  return {
    ...emptyRespiratory(),
    mode: str(d.mode),
    ventMode: str(d.ventMode),
    rate: str(d.rate),
    tidalVolumeMl: str(d.tidalVolumeMl),
    pip: str(d.pip),
    peep: str(d.peep),
    fio2: str(d.fio2),
    o2FlowLpm: str(d.o2FlowLpm),
    spo2: str(d.spo2),
    suctionCount: str(d.suctionCount),
    secretionAmount: str(d.secretionAmount),
    secretionColor: str(d.secretionColor),
    tolerance: str(d.tolerance),
    alarmsChecked: d.alarmsChecked === true,
    circuitChecked: d.circuitChecked === true,
    notes: str(d.notes),
  };
}

export function seizureForm(entry: Pick<Entry, 'data'>, fallbackTime: string): SeizureFormData {
  const d = dataOf(entry);
  const startedMs = Date.parse(str(d.startedAt));
  const secs = typeof d.durationSeconds === 'number' ? d.durationSeconds : null;
  return {
    ...emptySeizure(Number.isFinite(startedMs) ? toClockTime(startedMs) : fallbackTime),
    durationMinutes: secs !== null ? String(Math.floor(secs / 60)) : '',
    durationSeconds: secs !== null ? String(secs % 60) : '',
    seizureType: str(d.seizureType),
    description: str(d.description),
    interventions: Array.isArray(d.interventions) ? d.interventions.map(String) : [],
    rescueMedEntryId: str(d.rescueMedEntryId),
    postIctalState: str(d.postIctalState),
    notified: Array.isArray(d.notified) ? d.notified.map(String).join(', ') : '',
    notes: str(d.notes),
  };
}

export function outputForm(entry: Pick<Entry, 'data'>): OutputFormData {
  const d = dataOf(entry);
  return {
    ...emptyOutput(),
    outputType: str(d.outputType),
    volumeMl: str(d.volumeMl),
    diaperCount: str(d.diaperCount),
    characteristics: str(d.characteristics),
    notes: str(d.notes),
  };
}

export function supervisoryForm(entry: Pick<Entry, 'data'>): SupervisoryFormData {
  const d = dataOf(entry);
  return {
    ...emptySupervisory(),
    supervisedUserId: str(d.supervisedUserId),
    supervisedDiscipline: str(d.supervisedDiscipline),
    staffPresent: d.staffPresent === true ? 'YES' : d.staffPresent === false ? 'NO' : '',
    pocFollowed: str(d.pocFollowed),
    competenciesObserved: Array.isArray(d.competenciesObserved) ? d.competenciesObserved.map(String) : [],
    clientSatisfaction: str(d.clientSatisfaction),
    issues: str(d.issues),
    actions: str(d.actions),
    nextDueDate: str(d.nextDueDate),
  };
}

export function therapyForm(entry: Pick<Entry, 'data'>): TherapyFormData {
  const d = dataOf(entry);
  const lines = Array.isArray(d.lines) ? (d.lines as Data[]).map((l) => ({ code: str(l.code), minutes: str(l.minutes) })) : [];
  const goals = Array.isArray(d.goalsAddressed)
    ? (d.goalsAddressed as Data[]).map((g) => ({ goal: str(g.goal), goalId: str(g.goalId), progress: str(g.progress), note: str(g.note) }))
    : [];
  return {
    ...emptyTherapy(),
    evaluationCode: str(d.evaluationCode),
    lines: lines.length ? lines : emptyTherapy().lines,
    interventions: str(d.interventions),
    goalsAddressed: goals,
    homeProgram: str(d.homeProgram),
    patientResponse: str(d.patientResponse),
    coTreatment: d.coTreatment === true,
    coTreatmentWith: str(d.coTreatmentWith),
    telehealth: d.telehealth === true,
  };
}

export function aideTasksForm(entry: Pick<Entry, 'data'>): AideTasksFormData {
  const d = dataOf(entry);
  return {
    ...emptyAideTasks(),
    tasks: Array.isArray(d.tasks)
      ? (d.tasks as Data[]).map((t) => ({ task: str(t.task), status: str(t.status), minutes: str(t.minutes), note: str(t.note) }))
      : [],
    observations: str(d.observations),
    reportedToSupervisor: d.reportedToSupervisor === true,
  };
}

/** M8 "Copy last" types */
export const COPY_LAST_TYPES = ['vitals', 'respiratory', 'feed', 'output'] as const;
